// PWA Notification Hook
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Check if already subscribed
  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      setSubscription(sub);
      setIsSubscribed(!!sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      return 'unsupported';
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    if (!isSupported || permission !== 'granted') {
      console.log('Cannot subscribe: not supported or not permitted');
      return false;
    }

    try {
      // Get VAPID public key from backend
      const { data: vapidData } = await api.get('/notificacoes/vapid-public-key');
      const vapidPublicKey = vapidData.publicKey;

      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await api.post('/notificacoes/subscribe', sub.toJSON());
      
      setSubscription(sub);
      setIsSubscribed(true);
      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return false;
    }
  }, [isSupported, permission]);

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async () => {
    if (!subscription) return false;

    try {
      // Notify backend
      await api.delete('/notificacoes/unsubscribe', { data: subscription.toJSON() });
      
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      setSubscription(null);
      setIsSubscribed(false);
      console.log('Push unsubscribe successful');
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    }
  }, [subscription]);

  // Test push notification
  const testNotification = useCallback(async () => {
    try {
      await api.post('/notificacoes/test');
      return true;
    } catch (error) {
      console.error('Error testing notification:', error);
      return false;
    }
  }, []);

  // Show a local notification
  const showNotification = useCallback(async (title, options = {}) => {
    if (!isSupported || permission !== 'granted') {
      console.log('Notifications not available or not permitted');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      const defaultOptions = {
        icon: '/sterisul-logo.png',
        badge: '/sterisul-logo.png',
        tag: 'sterisul-notification',
        requireInteraction: true,
        ...options
      };

      await registration.showNotification(title, defaultOptions);
      return true;
    } catch (error) {
      console.error('Error showing notification:', error);
      return false;
    }
  }, [isSupported, permission]);

  // Notify about expiring materials
  const notifyExpiring = useCallback(async (items) => {
    if (items.length === 0) return;

    const title = `⚠️ ${items.length} material(is) próximo(s) do vencimento`;
    const body = items.slice(0, 3).map(i => i.nome || i.numero_ciclo).join(', ');
    
    await showNotification(title, {
      body: body + (items.length > 3 ? ` e mais ${items.length - 3}...` : ''),
      tag: 'expiring-materials',
      data: { type: 'validade', url: '/arsenal' },
      actions: [
        { action: 'view', title: 'Ver Arsenal' },
        { action: 'dismiss', title: 'Dispensar' }
      ]
    });
  }, [showNotification]);

  // Notify about cycle status change
  const notifyCicloStatus = useCallback(async (ciclo, status) => {
    const statusMessages = {
      'em_processo': '🔄 Ciclo em andamento',
      'finalizado': '✅ Ciclo finalizado',
      'aprovado': '✅ Ciclo aprovado',
      'reprovado': '❌ Ciclo reprovado'
    };

    const title = statusMessages[status] || `Ciclo ${status}`;
    
    await showNotification(title, {
      body: `${ciclo.numero_ciclo} - ${ciclo.autoclave_nome || 'Autoclave'}`,
      tag: `ciclo-${ciclo.id}`,
      data: { type: 'ciclo', url: '/ciclos' }
    });
  }, [showNotification]);

  // Notify about new agendamento
  const notifyAgendamento = useCallback(async (agendamento, type) => {
    const typeMessages = {
      'novo': '📅 Novo agendamento',
      'confirmado': '✅ Agendamento confirmado',
      'em_andamento': '🚗 Coleta/Entrega em andamento',
      'concluido': '✅ Agendamento concluído',
      'cancelado': '❌ Agendamento cancelado'
    };

    const title = typeMessages[type] || 'Agendamento atualizado';
    
    await showNotification(title, {
      body: `${agendamento.cliente_nome} - ${agendamento.tipo === 'coleta' ? 'Coleta' : 'Entrega'}`,
      tag: `agendamento-${agendamento.id}`,
      data: { type: 'agendamento', url: '/agendamentos' }
    });
  }, [showNotification]);

  return {
    permission,
    isSupported,
    subscription,
    isSubscribed,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    testNotification,
    showNotification,
    notifyExpiring,
    notifyCicloStatus,
    notifyAgendamento
  };
}

export default useNotifications;
